/**
 * Safe mathematical operations to prevent NaN and Infinity values
 */

/**
 * Safely divide two numbers, returning 0 if denominator is 0 or near 0
 * @param numerator - The number to be divided
 * @param denominator - The number to divide by
 * @param defaultValue - Value to return if division is unsafe (default: 0)
 * @returns The result of division or defaultValue
 */
export const safeDivide = (numerator: number, denominator: number, defaultValue: number = 0): number => {
  if (Math.abs(denominator) < 0.0001) {
    return defaultValue;
  }
  const result = numerator / denominator;
  if (!isFinite(result) || isNaN(result)) {
    return defaultValue;
  }
  return result;
};

/**
 * Safely calculate a percentage
 * @param part - The part value
 * @param whole - The whole value
 * @param defaultValue - Value to return if calculation is unsafe (default: 0)
 * @returns The percentage or defaultValue
 */
export const safePercentage = (part: number, whole: number, defaultValue: number = 0): number => {
  return safeDivide(part * 100, whole, defaultValue);
};

/**
 * Safely multiply and divide (a * b / c)
 * @param a - First multiplier
 * @param b - Second multiplier
 * @param c - Divisor
 * @param defaultValue - Value to return if operation is unsafe (default: 0)
 * @returns The result or defaultValue
 */
export const safeMultiplyDivide = (a: number, b: number, c: number, defaultValue: number = 0): number => {
  return safeDivide(a * b, c, defaultValue);
};

/**
 * Ensure a number is within a valid range
 * @param value - The value to clamp
 * @param min - Minimum allowed value
 * @param max - Maximum allowed value
 * @returns The clamped value
 */
export const clamp = (value: number, min: number, max: number): number => {
  return Math.max(min, Math.min(max, value));
};

/**
 * Check if a number is valid (not NaN, not Infinity)
 * @param value - The value to check
 * @returns true if valid, false otherwise
 */
export const isValidNumber = (value: number): boolean => {
  return isFinite(value) && !isNaN(value);
};

/**
 * Ensure a number is valid, otherwise return default
 * @param value - The value to check
 * @param defaultValue - Value to return if invalid (default: 0)
 * @returns Valid value or defaultValue
 */
export const ensureValidNumber = (value: number, defaultValue: number = 0): number => {
  return isValidNumber(value) ? value : defaultValue;
};